import { MEASURE_SLUGS } from "~/consts";
import { ExportTableNames } from "~/modules/downloads/types";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    AggregateQueryMode,
    ApiQuery,
    ApiQueryEndpoints,
    Filter,
    FilterOperators,
} from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS } from "~/modules/visualization/components/Timeseries/consts";
import {
    LegendProps,
    NavigatorControlState,
    VisualizationConfig,
    VisualizationMode,
    VisualizationModeTypes,
} from "~/modules/visualization/components/Timeseries/types";
import { TODAYS_DATE, TWO_YEARS_AGO } from "~/utils/dates/consts";

// // // //

const BASE_QUERY: ApiQuery = {
    sort: {
        direction: "asc",
        field: "date",
    },
    pagination: {
        page: 1,
        perPage: 25,
    },
};

const visualizationConfig: VisualizationConfig = {
    area: true,
    enableZoom: false,
    showTotal: true,
    height: 350,
    legend: {
        itemWidth: 125,
        layout: "vertical",
        align: "right",
        verticalAlign: "top",
    },
};

export function getMintBurnVolumeChart(props: {
    key: string;
    description?: string;
    filters: Filter[];
    legend?: LegendProps;
    navigator?: NavigatorControlState;
    height?: number;
    loginRequired?: boolean;
}): VisualizationMode {
    const {
        key,
        description,
        filters,
        legend = visualizationConfig.legend,
        navigator = {
            enabled: true,
            active: true,
            forceUpdate: true,
            xAxis: {
                min: Number(TWO_YEARS_AGO),
                max: Number(TODAYS_DATE),
            },
        },
        height,
        loginRequired = true,
    } = props;
    return {
        key,
        type: VisualizationModeTypes.mintBurnVolume,
        loginRequired: loginRequired ?? false,
        content: {
            label: "Fund Flows",
            description,
        },
        visualizationConfig: {
            ...visualizationConfig,
            legend,
            yAxis: {
                min: undefined,
            },
            ...(height !== undefined && { height }),
        },
        visualizationControls: {
            stacked: {
                enabled: false,
                active: false,
            },
            legend: {
                enabled: true,
                active: false,
                forceUpdate: true,
            },
            navigator: {
                ...navigator,
            },
        },
        query: {
            ...BASE_QUERY,
            aggregate: {
                aggregateFunction: AggregateFunctions.Sum,
                groupBy: TokenTimeseriesGroupBy.measure,
                interval: AggregateQueryInterval.month,
                mode: AggregateQueryMode.flow,
            },
            filter: {
                operator: FilterOperators.And,
                filters: [
                    ...filters,
                    {
                        operator: FilterOperators.Or,
                        filters: [
                            {
                                field: TokenTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.adjusted_daily_mints_dollar,
                            },
                            {
                                field: TokenTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.adjusted_daily_burns_dollar,
                            },
                        ],
                    },
                ],
            },
        },
        queryControls: {
            groupBy: {
                enabled: false,
                forceUpdate: true,
                value: TokenTimeseriesGroupBy.measure,
                allowedOptions: [TokenTimeseriesGroupBy.measure],
            },
            interval: {
                enabled: true,
                forceUpdate: true,
                value: AggregateQueryInterval.month,
                allowedOptions: [
                    AggregateQueryInterval.day,
                    AggregateQueryInterval.week,
                    AggregateQueryInterval.month,
                    AggregateQueryInterval.quarter,
                    AggregateQueryInterval.year,
                ],
            },
        },
        endpoint: ApiQueryEndpoints.TokenTimeseriesV4,
        apiTools: TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS,
        dataExportControls: {
            enabled: true,
            filename: key,
            exportTableName: ExportTableNames.token_timeseries_v4,
        },
    };
}
